import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import {
  hero,
  introStatement,
  uspSection,
  stats,
  disciplinesSection,
  disciplines,
  environments,
  closingCta,
} from '../mock';

const Home = () => {
  const ref = useReveal();

  return (
    <div ref={ref}>
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] w-full">
        <div className="img-hover-zoom absolute inset-0">
          <img src={hero.image} alt="Professionelt byggeri" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-ink/30" />
        <div className="relative h-full mx-auto max-w-[1500px] px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-28">
          <div className="w-full max-w-5xl text-bone">
            <p className="text-[11px] uppercase tracking-[0.35em] mb-6 text-bone/80">
              {hero.eyebrow}
            </p>
            <h1 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.08]">
              {hero.title}
            </h1>
            <p className="mt-8 max-w-xl text-base md:text-lg font-light leading-relaxed text-bone/85">
              {hero.subtitle}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-8">
              <Link
                to={hero.cta1.path}
                className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] border-b border-bone/60 pb-1 hover:border-bone transition-colors"
              >
                {hero.cta1.label} <ArrowRight size={14} strokeWidth={1.4} />
              </Link>
              <Link
                to={hero.cta2.path}
                className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-bone/80 hover:text-bone transition-colors"
              >
                {hero.cta2.label}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO STATEMENT */}
      <section className="bg-bone">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12 py-28 md:py-40 text-center reveal">
          <p className="font-serif font-light text-2xl md:text-4xl leading-[1.4] text-ink">
            {introStatement}
          </p>
        </div>
      </section>

      {/* USP SPLIT */}
      <section className="bg-paper border-y border-ink/10">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="order-2 md:order-1 px-6 md:px-12 lg:px-20 py-20 md:py-32 flex flex-col justify-center">
            <div className="reveal max-w-lg">
              <p className="text-[11px] uppercase tracking-[0.3em] text-stone">{uspSection.eyebrow}</p>
              <h2 className="mt-5 font-serif font-light text-4xl md:text-5xl">{uspSection.title}</h2>
              <div className="mt-12">
                {uspSection.items.map((u) => (
                  <div key={u.no} className="border-t border-ink/20 py-8">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-clay">USP {u.no}</div>
                    <p className="mt-4 font-serif font-light text-2xl md:text-3xl leading-snug">
                      {u.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="order-1 md:order-2 img-hover-zoom min-h-[360px] md:min-h-full">
            <img src={uspSection.image} alt="Kepp & Co." className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-bone">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
            {stats.map((s) => (
              <div key={s.label} className="reveal border-t border-ink/15 pt-8">
                <div className="font-serif font-light text-2xl md:text-3xl leading-tight">{s.value}</div>
                <div className="mt-4 text-[11px] uppercase tracking-[0.2em] text-stone">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DISCIPLINES */}
      <section className="bg-ink text-bone">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 py-24 md:py-32">
          <div className="reveal mb-14 md:mb-20 max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.3em] text-bone/40">{disciplinesSection.eyebrow}</p>
            <h2 className="mt-5 font-serif font-light text-4xl md:text-5xl">{disciplinesSection.title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {disciplines.map((d) => (
              <div key={d.title} className="reveal">
                <div className="img-hover-zoom aspect-[3/4] w-full">
                  <img src={d.image} alt={d.title} className="h-full w-full object-cover" />
                </div>
                <div className="mt-6 text-[11px] uppercase tracking-[0.25em] text-clay">{d.no}</div>
                <h3 className="mt-2 font-serif text-2xl md:text-3xl font-light">{d.title}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-bone/60">{d.short}</p>
              </div>
            ))}
          </div>
          <div className="reveal mt-16">
            <Link
              to={disciplinesSection.cta.path}
              className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] border-b border-bone/50 pb-1 hover:border-bone transition-colors"
            >
              {disciplinesSection.cta.label} <ArrowRight size={14} strokeWidth={1.4} />
            </Link>
          </div>
        </div>
      </section>

      {/* ENVIRONMENTS */}
      <section className="bg-bone">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 py-24 md:py-32">
          <div className="reveal mb-14 max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.3em] text-stone">{environments.eyebrow}</p>
            <h2 className="mt-5 font-serif font-light text-4xl md:text-5xl">{environments.title}</h2>
            <p className="mt-5 text-sm md:text-base font-light leading-relaxed text-stone">
              {environments.subtitle}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {environments.categories.map((c) => (
              <div key={c.title} className="reveal">
                <div className="img-hover-zoom aspect-[3/4] w-full">
                  <img src={c.image} alt={c.title} className="h-full w-full object-cover" />
                </div>
                <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-stone">{c.title}</p>
              </div>
            ))}
          </div>
          <p className="reveal mt-10 text-xs font-light italic text-stone/80">{environments.note}</p>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-paper border-t border-ink/10">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12 py-28 md:py-40 text-center reveal">
          <h2 className="font-serif font-light text-4xl md:text-6xl leading-tight">{closingCta.title}</h2>
          <Link
            to={closingCta.path}
            className="mt-10 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] border-b border-ink/50 pb-1 hover:border-ink transition-colors"
          >
            {closingCta.label} <ArrowRight size={14} strokeWidth={1.4} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
